import {
    useEffect,
    useState
} from "react";

import GraphQLLocationDAOFactory from "../../lib/location/dao/graphql/GraphQLLocationDAOFactory";
import { GraphQLSearchLocationByPropertyIdOperation } from "../../lib/location/dao/GraphQLSearchLocationByPropertyIdOperation";
import { Location } from "../../models";
import LocationDAO from "../../lib/location/dao/LocationDAO";
import { SearchBox } from "../misc/SearchBox";
import { TextFieldProps } from "@mui/material";

type LocationSearchBoxProps = {
    readonly propertyId: string | undefined;
    readonly onNewOptionCreated?: (location: Location) => void;
    readonly onSelect: (option: Location | null) => void;
    readonly onClear: () => void;
    readonly label: string;
    readonly disabled?: boolean;
    readonly initialLocationId: string | undefined;
    readonly locationName?: string;
    readonly isNewLocation?: boolean;
    readonly typeName?: string;
} & Omit<TextFieldProps, "onSelect" | "value">;

const locationDAO: LocationDAO & GraphQLSearchLocationByPropertyIdOperation = GraphQLLocationDAOFactory.getInstance();

export const LocationSearchBox = (props: LocationSearchBoxProps) => {
    const {
        propertyId,
        onSelect,
        onClear,
        label,
        disabled,
        initialLocationId,
        onNewOptionCreated,
        locationName,
        isNewLocation,
        typeName = "Group",
        ...textFieldProps
    } = props;

    const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    useEffect(() => {
        if (isInitialized) {
            setSelectedLocation(null);
            onClear();
        }
    }, [propertyId]);

    useEffect(() => {
        const initializeLocation = async () => {
            if (initialLocationId && !isNewLocation) {
                const location: Location = await locationDAO.getLocationById(initialLocationId);
                setSelectedLocation(location);
            } else if (isNewLocation) {
                const newLocation = new Location({ propertyId: propertyId!, name: locationName });
                setSelectedLocation(newLocation);
            }
            setIsInitialized(true);
        };
        initializeLocation();
    }, [initialLocationId, isNewLocation]);

    return (
        <SearchBox
            label={label}
            typeName={typeName}
            labelFields={["name"]}
            value={selectedLocation}
            onClear={() => {
                setSelectedLocation(null);
                onClear();
            }}
            onSelect={(option: Location | null) => {
                onSelect(option);
                setSelectedLocation(option);
            }}
            onNewOptionSelected={async (name: string) => {
                const location = new Location({ propertyId: propertyId!, name: name || "" });
                if (onNewOptionCreated) {
                    onNewOptionCreated(location);
                } else {
                    onSelect(location);
                }
                setSelectedLocation(location);
            }}
            search={(searchValue, nextToken) => {
                return locationDAO.searchLocationsByPropertyId(propertyId!, searchValue, nextToken);
            }}
            queryKeys={propertyId ? [propertyId] : []}
            textFieldStyle={textFieldProps}
            searchWithoutSearchTerm={!disabled}
            disabled={disabled}
            freeSolo={true}
        />
    );
};

export default LocationSearchBox;
